import { Button } from '@thanx/uikit/button'
import heroPromo from 'assets/images/recovery-ai/img_hero_promo.svg'
import React, { useRef } from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { generateId } from 'utilities/uniqueId'

// Two-pane "turn on RecoveryAI" upsell card (AI-241). Promoted into components/ so
// a non-RAI scene can reuse the hero without a cross-scene import — hence
// presentational: eyebrow/CTA copy and navigation all arrive as props (no dispatch
// here). The dollar-upside hero #3712 carried has a backend source now
// (hooks/useROICalculator); the TUTD Details tab surfaces it via the optional
// `stats` row below, pre-translated/pre-formatted by the caller just like `eyebrow`.
// Copy is MKT-owned (AI-128).
//
// context: two consumers — Overview (above the pilot Looker dashboard while
//   coverage < 100%) and the TUTD Details tab (shown to merchants without RAI,
//   Figma 790:14527, with the ROI stats row Overview does not use).

// 22px, not the uikit scale, because the eyebrow stays a plain <span>: uikit's Text
// only forwards className, so a Text.* wrapper would silently drop the eyebrow id and
// break the CTA's aria-describedby. 22 is a step on the repo's legacy font-size scale.
const EYEBROW_FONT_SIZE_PX = 22
// Intrinsic size of the hero raster (it is a 280x260 PNG wrapped in SVG, no vector
// paths — scaling a 1x raster only softens it). Design runs it flush to the card's
// top and bottom edges; the artwork is authored pre-cut there (flat arrow tips,
// sliced coin stacks), so zero vertical padding gives the bleed for free. It is
// also the tallest child, so it sets the card height.
const HERO_WIDTH_PX = 280
const HERO_HEIGHT_PX = 260
const CARD_PADDING_LEFT_PX = 24
const CARD_BORDER_RADIUS_PX = 8
const CARD_GAP_PX = 72
// grey90 at 20%, offset down — lifts the card off the grey05 page. Matches the
// measured Figma shadow and reuses the value already in AiSuggestionsBanner.tsx.
// Inline rgba because shadows have no theme token and there is no hex→rgba helper.
const CARD_SHADOW = '0px 8px 24px rgba(54, 66, 78, 0.2)'
// The layout needs 24 + 280 + 72 + 350 (CTA) + 32 = 758px of card width. Only the
// bootstrap `lg` container (960px) clears that; `md` gives 720px and would clip the
// CTA. So hide below `lg`. The illustration is decorative — dropping it beats
// shrinking it, which would rescale the artwork and lose the flush edges.
// Re-measure this if the CTA label gets longer: it is the term that dominates.
const HERO_HIDE_BELOW_PX = 991
// Only a floor: the copy pane measures well under HERO_HEIGHT_PX, so `center`
// leaves it untouched. It stops the copy touching the card edges if the pane ever
// outgrows the illustration (long i18n, narrow viewport).
const COPY_PANE_PADDING_PX = 32
const COPY_GAP_PX = 12
const CTA_MARGIN_TOP_PX = 8
const CTA_LABEL_GAP_PX = 8

// headlineEmphasis (Figma "Heading/Header 3"): 24/28 bold, vs. the plain
// eyebrow's 22px regular that Overview still uses unmodified.
const HEADLINE_EMPHASIS_FONT_SIZE_PX = 24
const HEADLINE_EMPHASIS_LINE_HEIGHT_PX = 28
const HEADLINE_EMPHASIS_FONT_WEIGHT = 700

// ROI stats row (Figma 790:14527 "RevenueSection"). Two ~193px columns with a
// hairline divider between them; each column is a value+suffix line over a label.
const STATS_ROW_GAP_PX = 24
const STAT_BLOCK_WIDTH_PX = 193
const STAT_BLOCK_GAP_PX = 8
const STAT_DIVIDER_WIDTH_PX = 1
const STAT_VALUE_FONT_SIZE_PX = 32
const STAT_VALUE_LINE_HEIGHT_PX = 40
// Lato Black — the design's heaviest weight, distinct from HEADLINE_EMPHASIS_FONT_WEIGHT.
const STAT_VALUE_FONT_WEIGHT = 900
const STAT_SUFFIX_FONT_SIZE_PX = 16
const STAT_SUFFIX_LINE_HEIGHT_PX = 24
const STAT_LABEL_FONT_SIZE_PX = 16
const STAT_LABEL_LINE_HEIGHT_PX = 24

type Stat = {
  // Pre-formatted by the caller (e.g. via utilities/formatters `currencyFormatter`)
  // — same contract as `eyebrow`: this component does no formatting of its own.
  value: string
  suffix: string
  label: string
}

type Props = {
  // Pre-translated by the caller. Kept the name `eyebrow` (not `headline`) because
  // eyebrowId / EYEBROW_FONT_SIZE_PX and its comments already use it; the TUTD
  // surface reads this slot as a headline rather than a coverage eyebrow.
  eyebrow: string
  ctaLabel: string
  onCtaClick: () => void
  // TUTD-only: the ROI figures rendered between the headline and the CTA. Absent
  // for Overview, so its render stays byte-identical to before this row existed.
  stats?: ReadonlyArray<Stat>
  // TUTD-only: renders `eyebrow` as the larger, bold TUTD headline instead of
  // Overview's plain 22px eyebrow. Defaults to false so Overview is unaffected.
  headlineEmphasis?: boolean
  // Overview-only (RBAC): a view-only ('access', not 'manage') RecoveryAI
  // operator can reach the Overview tab but must not be able to fire the CTA's
  // navigation into the edit-only Active Locations settings pane. Defaults to
  // false so the TUTD consumer (OrderFeedbackReport/DetailsPane, no ability
  // gate) and the existing "enabled by default" Billboard tests are unaffected.
  // A real `disabled` uikit Button attribute is correct here (not
  // aria-disabled) — the CTA is a native <button>, not an anchor.
  disabled?: boolean
}

const Billboard = ({
  eyebrow,
  ctaLabel,
  onCtaClick,
  stats,
  headlineEmphasis = false,
  disabled = false,
}: Props) => {
  const [css] = useStyletron()

  // Per-instance id ties the eyebrow to the CTA via aria-describedby. Generated
  // (not a module constant) because this is a shared components/ primitive now — two
  // Billboards on one page with a static id would silently break both cards' describedby.
  const eyebrowId = useRef(`billboard-eyebrow-${generateId()}`).current

  return (
    <div
      // mb-l is 2rem = exactly CARD_SHADOW's reach (8px offset + 24px blur), so the
      // shadow stops short of the Looker section's "Performance" heading, which
      // otherwise starts flush against the card's bottom edge.
      className={`d-flex align-items-center mb-l ${css({
        borderRadius: `${CARD_BORDER_RADIUS_PX}px`,
        // Light lavender card (not a full-bleed purple) so it reads as two panes
        // against the grey05 page. wisteria05, not wisteria10: the design's tint is
        // a pink-leaning near-white, where wisteria10 leans blue.
        backgroundColor: merchantTheme.colors.wisteria05,
        boxShadow: CARD_SHADOW,
        // No vertical padding — see HERO_HEIGHT_PX. Clipping only guards the radius
        // if a child ever outgrows the card.
        paddingLeft: `${CARD_PADDING_LEFT_PX}px`,
        overflow: 'hidden',
        gap: `${CARD_GAP_PX}px`,
      })}`}
    >
      {/*
        Decorative revenue-growth illustration (AI-128). alt="" AND aria-hidden:
        this repo's aria-query version still matches `img[alt]`, so alt="" alone
        leaves it in the a11y tree — aria-hidden is what removes it. The copy pane
        carries the whole message, so nothing is lost.
      */}
      <img
        src={heroPromo}
        alt=""
        aria-hidden="true"
        className={css({
          width: `${HERO_WIDTH_PX}px`,
          height: `${HERO_HEIGHT_PX}px`,
          flexShrink: 0,
          [`@media (max-width: ${HERO_HIDE_BELOW_PX}px)`]: { display: 'none' },
        })}
      />

      {/* Copy + interactive pane */}
      <div
        className={`d-flex flex-column ${css({
          gap: `${COPY_GAP_PX}px`,
          flex: '1 1 auto',
          paddingTop: `${COPY_PANE_PADDING_PX}px`,
          paddingBottom: `${COPY_PANE_PADDING_PX}px`,
          paddingRight: `${COPY_PANE_PADDING_PX}px`,
        })}`}
      >
        <span
          id={eyebrowId}
          className={css({
            // headlineEmphasis (TUTD only) steps up to the bold 24/28 Figma
            // headline; Overview never passes it, so its render is unchanged.
            fontSize: headlineEmphasis
              ? `${HEADLINE_EMPHASIS_FONT_SIZE_PX}px`
              : `${EYEBROW_FONT_SIZE_PX}px`,
            ...(headlineEmphasis && {
              lineHeight: `${HEADLINE_EMPHASIS_LINE_HEIGHT_PX}px`,
              fontWeight: HEADLINE_EMPHASIS_FONT_WEIGHT,
            }),
            // grey90 (not grey70): with the upside hero dropped this is the
            // card's primary line, so it takes the strongest colour — 9.9:1 on
            // wisteria05. grey70 would clear AA on this tint (4.59:1) but only
            // barely, and it failed outright on the older wisteria10 (4.21:1).
            color: merchantTheme.colors.grey90,
          })}
        >
          {eyebrow}
        </span>

        {/*
          ROI stats row (TUTD only, Figma 790:14527). Two columns separated by a
          hairline divider that stretches to the row's height via the row's
          default flex `align-items: stretch` — no explicit divider height needed.
        */}
        {stats && stats.length > 0 && (
          <div className={`d-flex ${css({ gap: `${STATS_ROW_GAP_PX}px` })}`}>
            {stats.map((stat, index) => (
              <React.Fragment key={stat.label}>
                {index > 0 && (
                  <div
                    className={css({
                      width: `${STAT_DIVIDER_WIDTH_PX}px`,
                      backgroundColor: merchantTheme.colors.grey30,
                    })}
                  />
                )}
                <div
                  className={`d-flex flex-column ${css({
                    gap: `${STAT_BLOCK_GAP_PX}px`,
                    width: `${STAT_BLOCK_WIDTH_PX}px`,
                  })}`}
                >
                  <div>
                    <span
                      className={css({
                        fontSize: `${STAT_VALUE_FONT_SIZE_PX}px`,
                        lineHeight: `${STAT_VALUE_LINE_HEIGHT_PX}px`,
                        fontWeight: STAT_VALUE_FONT_WEIGHT,
                        color: merchantTheme.colors.grey90,
                      })}
                    >
                      {stat.value}
                    </span>
                    <span
                      className={css({
                        fontSize: `${STAT_SUFFIX_FONT_SIZE_PX}px`,
                        lineHeight: `${STAT_SUFFIX_LINE_HEIGHT_PX}px`,
                        color: merchantTheme.colors.grey70,
                      })}
                    >
                      {stat.suffix}
                    </span>
                  </div>
                  <span
                    className={css({
                      fontSize: `${STAT_LABEL_FONT_SIZE_PX}px`,
                      lineHeight: `${STAT_LABEL_LINE_HEIGHT_PX}px`,
                      color: merchantTheme.colors.grey70,
                    })}
                  >
                    {stat.label}
                  </span>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        <div className={css({ marginTop: `${CTA_MARGIN_TOP_PX}px` })}>
          {/*
            CTA fires onCtaClick — each scene owns its own navigation (Overview
            deep-links to Settings → Active Locations; the TUTD tab routes into
            RecoveryAI). aria-describedby ties it to the eyebrow so the context is
            announced on focus. Copy remains MKT-owned (AI-128).
          */}
          <Button
            kind="primary"
            type="button"
            aria-describedby={eyebrowId}
            onClick={onCtaClick}
            disabled={disabled}
          >
            <span
              className={`d-flex align-items-center ${css({
                gap: `${CTA_LABEL_GAP_PX}px`,
              })}`}
            >
              {ctaLabel}
              <i className="fa fa-arrow-right" aria-hidden="true" />
            </span>
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Billboard
